import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Typography from '../Typography';
import Button from '../Button/Button';
import CloneEventModal from './CloneEventModal';
import CancelEventModal from './CancelEventModal';
import { useTranslation } from 'react-i18next';
import { theme } from '../../theme';
import { toast } from 'react-toastify';
import { useEventContext } from '../../context/EventContext';
import axiosClient from '../../api/axiosClient';
import CopyClipboardOutlineIcon from '../Icons/CopyClipboardOutlineIcon';
import EyeOffIcon from '../Icons/EyeOffIcon';
import MotorikCircle from '../Icons/MotorikCircle';
import { isEventPast } from '../../utils/dateUtils';

const ManageEventOptions = () => {
  const { t } = useTranslation('manageEventOptions');
  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const { eventDetails: event, fetchMyEvents, updateEventDetails } = useEventContext();
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancellationType, setCancellationType] = useState(null);
  const [attendeesInfo, setAttendeesInfo] = useState({ total: 0, paid: 0 });

  const handleCloneEvent = async (dateData) => {
    setIsCloning(true);
    try {
      const response = await axiosClient.post(`/api/events/${eventId}/clone`, dateData);
      const data = response.data;

      if (data.success) {
        toast.success(t('cloneEvent.success'));
        setIsCloneModalOpen(false);
        // Navegar al evento clonado para editarlo
        navigate(`/events/manage/${data.event._id}`);
      } else {
        toast.error(data.message || t('cloneEvent.error'));
      }
    } catch (error) {
      console.error('Error cloning event:', error);
      toast.error(t('cloneEvent.error'));
    } finally {
      setIsCloning(false);
    }
  };

  const handleUnpublish = async () => {
    try {
      const response = await axiosClient.post(`/api/events/${eventId}/unpublish`);
      if (response.data.success) {
        toast.success('Evento despublicado exitosamente');
        fetchMyEvents(); // Actualizar los datos del evento

        // Actualizar eventDetails con el estado despublicado
        updateEventDetails({
          ...event,
          published: false
        });
      } else {
        toast.error('Error al despublicar el evento');
      }
    } catch (error) {
      console.error('Error al despublicar el evento:', error);
      toast.error('Error al cambiar la visibilidad del evento');
    }
  };

  const handlePublish = async () => {
    try {
      const response = await axiosClient.post(`/api/events/${eventId}/publish`);
      if (response.data.success) {
        toast.success('Evento publicado exitosamente');
        fetchMyEvents(); // Actualizar los datos del evento

        // Actualizar eventDetails con el estado publicado
        updateEventDetails({
          ...event,
          published: true
        });
      }
    } catch (error) {
      console.error('Error al publicar el evento:', error);
      toast.error('Error al cambiar la visibilidad del evento');
    }
  };

  const handleCancelEvent = async () => {
    if (!event) return;

    // Determinar el tipo de cancelación basado en los asistentes
    const attendees = event.attendees || [];
    const attendingUsers = attendees.filter(attendee => attendee.status === 'attending');
    const paidAttendees = attendingUsers.filter(attendee =>
      attendee.ticketType && attendee.ticketType.price > 0
    );

    let type;
    if (attendingUsers.length === 0) {
      type = 'no_attendees';
    } else if (paidAttendees.length === 0) {
      type = 'free_only';
    } else {
      type = 'paid_attendees';
    }

    setCancellationType(type);
    setAttendeesInfo({
      total: attendingUsers.length,
      paid: paidAttendees.length
    });
    setIsCancelModalOpen(true);
  };

  const handleConfirmCancel = async () => {
    setIsCancelling(true);
    try {
      const response = await axiosClient.post(`/api/events/${eventId}/cancel`, {
        reason: `Cancelado por el organizador - Tipo: ${cancellationType}`
      });

      if (response.data.success) {
        const messages = {
          no_attendees: t('cancelEvent.success.noAttendees'),
          free_only: t('cancelEvent.success.freeOnly'),
          paid_attendees: t('cancelEvent.success.paidAttendees')
        };

        toast.success(messages[cancellationType] || t('cancelEvent.success.default'));
        setIsCancelModalOpen(false);

        // Actualizar tanto myEvents como eventDetails
        fetchMyEvents();

        // Actualizar eventDetails con el estado cancelado
        updateEventDetails({
          ...event,
          cancelled: true,
          status: 'cancelled'
        });

        // Mantener al usuario en la vista de gestión del evento
        // La redirección automática se ha eliminado para mejorar la UX
      } else {
        toast.error(response.data.message || t('cancelEvent.error'));
      }
    } catch (error) {
      console.error('Error cancelling event:', error);
      toast.error(t('cancelEvent.error'));
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <EventOptionsContainer>
      <Container>
        {/* <Typography $variant="title-4-semibold" color={theme.colors.defaultStrong}>
          Opciones del evento
        </Typography> */}
        <PageHeader>
          <Typography $variant="title-3-semibold" $variantTablet="title-4-semibold" $variantMobile="body-1-semibold" as='h2'>
            {t('manageEventOptions.title')}
          </Typography>
        </PageHeader>




        <OptionsWrapper>
          <EventVisibility>
            <StatusWrapper>
              <Typography $variant="body-2-medium" $variantTablet="body-2-medium" $variantMobile="body-2-medium" color={theme.colors.defaultWeak}>
                Visibilidad del evento
              </Typography>
              <StatusBlock>
                {event?.published ? <StatusIndicator /> : <StatusIndicatorHidden />}
                <Typography $variant="title-5-semibold" $variantTablet="title-5-semibold" $variantMobile="body-1-semibold" color={theme.colors.defaultMain}>
                  {event?.published ? 'Publicado en la plataforma' : 'Privado'}
                </Typography>
              </StatusBlock>
            </StatusWrapper>
            {event?.published ? (
              <Button
                $variant="outline"
                size="l"
                sizeTablet="l"
                sizeMobile="m"
                onClick={handleUnpublish}
                disabled={event?.status === 'cancelled' || isEventPast(event?.endDate)}
              >
                <EyeOffIcon height={20} width={20} fill={theme.colors.defaultStrong} />
                Ocultar evento
              </Button>
            ) : (
              <Button
                $variant=""
                size="l"
                sizeTablet="l"
                sizeMobile="m"
                onClick={handlePublish}
                disabled={event?.status === 'cancelled' || isEventPast(event?.endDate)}
              >
                <MotorikCircle height={20} width={20} fill={theme.colors.defaultStrong} />
                Publicar evento
              </Button>
            )}
          </EventVisibility>
          <OptionCard>
            <OptionContent>
              <Typography as="p" $variant="title-4-semibold" $variantTablet="title-5-semibold" $variantMobile="body-1-semibold" color={theme.colors.defaultStrong}>
                {t('cloneEvent.title')}
              </Typography>
              <Typography as="p" $variant="body-1-medium" $variantTablet="body-1-medium" $variantMobile="body-2-medium" color={theme.colors.defaultWeak}>
                {t('cloneEvent.description')}
              </Typography>
            </OptionContent>
            <Button
              $variant="outline"
              size="l"
              sizeTablet="l"
              sizeMobile="m"
              onClick={() => setIsCloneModalOpen(true)}
              // disabled={!event || isEventPast(event?.endDate)}
              disabled={!event}
            >
              <CopyClipboardOutlineIcon height={16} width={16} fill={theme.colors.defaultStrong} />
              {t('cloneEvent.confirm')}
            </Button>
          </OptionCard>

          <OptionCard>
            <OptionContent>
              <Typography as="p" $variant="title-4-semibold" $variantTablet="title-5-semibold" $variantMobile="body-1-semibold" color={theme.colors.errorMain}>
                {t('cancelEvent.title')}
              </Typography>
              <Typography as="p" $variant="body-1-medium" $variantTablet="body-1-medium" $variantMobile="body-2-medium" color={theme.colors.defaultWeak}>
                {t('cancelEvent.description')}
              </Typography>
            </OptionContent>
            <Button
              $variant="outlineDanger"
              size="l"
              sizeTablet="l"
              sizeMobile="m"
              onClick={handleCancelEvent}
              disabled={!event || event?.status === 'cancelled' || isEventPast(event?.endDate)}
            >
              {t('cancelEvent.title')}
            </Button>
          </OptionCard>
        </OptionsWrapper>
      </Container>

      <CloneEventModal
        isOpen={isCloneModalOpen}
        onClose={() => setIsCloneModalOpen(false)}
        onConfirm={handleCloneEvent}
        eventTitle={event?.title}
        isLoading={isCloning}
      />

      <CancelEventModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleConfirmCancel}
        eventTitle={event?.title}
        isLoading={isCancelling}
        cancellationType={cancellationType}
        attendeesCount={attendeesInfo.total}
        paidAttendeesCount={attendeesInfo.paid}
      />
    </EventOptionsContainer>
  );
};

export default ManageEventOptions;

const EventOptionsContainer = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-top: ${({ theme }) => theme.sizing.sm};
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  // gap: ${({ theme }) => theme.sizing.md};
  max-width: 1400px;
  padding: 0px ${({ theme }) => theme.sizing.md};
  padding-bottom: 0;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0px ${({ theme }) => theme.sizing.sm};
  }
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: row;
  min-height: 40px;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: 32px;
  }
`;

const OptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  // gap: ${({ theme }) => theme.sizing.sm};
`;

const OptionCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.lg} 0px;
  transition: all 0.2s ease-in-out;
  // border-top: 1px solid ${theme.border.defaultSubtle};
  // flex-basis: calc(50% - ${({ theme }) => theme.sizing.sm});

  // &:first-child {
  //   border-top: none;
  // }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    
  }
`;

const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  flex: 1;
`;

const EventVisibility = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: ${({ theme }) => theme.sizing.sm};
  border-radius: ${({ theme }) => theme.radius.xs};
  background: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  margin-top: ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px};
    gap: ${({ theme }) => theme.sizing.sm};
  }
  
`;


const StatusWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const StatusBlock = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const StatusIndicator = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ theme }) => theme.fill.successMain}; 
`;

const StatusIndicatorHidden = styled(StatusIndicator)`
  background: red;
`;

